import { useState, useEffect } from "react";
import { Star, Upload, Loader2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useClientSession } from "@/hooks/useClientSession";
import { motion } from "framer-motion";
import { fetchPublicAnuncios } from "@/lib/public-anuncios";

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

interface AvaliarFormProps {
  avaliadoId: string;
  avaliadoApelido: string;
  onSuccess?: () => void;
  onRequireLogin?: () => void;
  defaultItems?: string[];
  defaultDate?: string;
}

const AvaliarForm = ({ avaliadoId, avaliadoApelido, onSuccess, onRequireLogin, defaultItems, defaultDate }: AvaliarFormProps) => {
  const { session } = useClientSession();

  const [nota, setNota] = useState<number>(0);
  const [texto, setTexto] = useState("");
  const [dataNegociacao, setDataNegociacao] = useState(defaultDate || "");
  const [anexos, setAnexos] = useState<File[]>([]);
  const [itensSelected, setItensSelected] = useState<string[]>(defaultItems || []);
  const [itemSearch, setItemSearch] = useState("");
  const [allItems, setAllItems] = useState<Array<{ name: string; image_url: string | null }>>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const loadItems = async () => {
      const anuncios = await fetchPublicAnuncios({ advertiserId: avaliadoId });

      if (anuncios.length === 0) {
        setAllItems([]);
        return;
      }

      const uniqueNames = [...new Set(anuncios.map((a) => a.item_nome).filter(Boolean))] as string[];
      const uniqueItemIds = [...new Set(anuncios.map((a) => a.item_id).filter(Boolean))] as string[];

      let itemsWithImages: Array<{ name: string; image_url: string | null }> = [];
      if (uniqueItemIds.length > 0) {
        const { data: tibiaItems } = await supabase
          .from("tibia_items")
          .select("name, image_url")
          .in("id", uniqueItemIds);
        const imgMap: Record<string, string | null> = {};
        for (const ti of tibiaItems || []) {
          imgMap[ti.name] = ti.image_url;
        }
        itemsWithImages = uniqueNames.map((name) => ({
          name,
          image_url: imgMap[name] || null,
        }));
      } else {
        itemsWithImages = uniqueNames.map((name) => ({ name, image_url: null }));
      }

      itemsWithImages.sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
      setAllItems(itemsWithImages);
    };
    loadItems();
  }, [avaliadoId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (anexos.length + files.length > 5) {
      toast.error("Máximo de 5 imagens");
      return;
    }
    setAnexos([...anexos, ...files]);
  };

  const removeAnexo = (idx: number) => {
    setAnexos(anexos.filter((_, i) => i !== idx));
  };

  const filteredItems = allItems.filter(it =>
    itemSearch ? it.name.toLowerCase().includes(itemSearch.toLowerCase()) : true
  ).slice(0, 50);

  const handleSubmit = async () => {
    if (!session) {
      onRequireLogin?.();
      return;
    }
    if (nota === 0 && !confirm("Deseja enviar com nota 0?")) return;
    if (itensSelected.length === 0) {
      toast.error("Selecione ao menos 1 item negociado");
      return;
    }
    if (anexos.length === 0) {
      toast.error("Anexe ao menos 1 imagem comprovando a transação");
      return;
    }

    setSubmitting(true);
    try {
      // Upload files
      const urls: string[] = [];
      for (const file of anexos) {
        const ext = file.name.split(".").pop();
        const path = `${avaliadoId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("avaliacoes-anexos").upload(path, file);
        if (upErr) throw new Error("Erro no upload de imagem");
        const { data: urlData } = supabase.storage.from("avaliacoes-anexos").getPublicUrl(path);
        urls.push(urlData.publicUrl);
      }

      const { data, error } = await supabase.functions.invoke("submit-avaliacao", {
        body: {
          avaliado_id: avaliadoId,
          avaliador_contato: session.contato,
          contato: session.contato,
          token: session.token,
          nota,
          texto: texto.trim() || null,
          itens_negociados: itensSelected,
          anexos_urls: urls,
          data_negociacao: dataNegociacao || null,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setSubmitted(true);
      toast.success("Avaliação enviada! Aguardando aprovação.");
      onSuccess?.();
    } catch (err: any) {
      toast.error(err.message || "Erro ao enviar avaliação");
    }
    setSubmitting(false);
  };

  if (!session) {
    return (
      <div className="text-center py-6 space-y-3">
        <p className="text-sm text-muted-foreground">Faça login na área do cliente para enviar uma avaliação.</p>
        <button onClick={onRequireLogin} className="tibia-btn rounded-sm font-pixel text-[8px]">Ir para Login</button>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="text-center py-6 space-y-3">
        <Star className="w-10 h-10 text-primary mx-auto" />
        <p className="text-sm text-muted-foreground">Avaliação enviada e aguardando aprovação.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Nota */}
      <div className="space-y-1">
        <label className="font-pixel text-[8px] text-muted-foreground">Nota (0-10)</label>
        <div className="flex items-center gap-1 flex-wrap">
          {Array.from({ length: 11 }, (_, i) => (
            <button
              key={i}
              onClick={() => setNota(i)}
              className={`w-6 h-6 rounded-sm font-pixel text-[7px] transition-all cursor-pointer ${
                i <= nota ? "bg-primary text-primary-foreground" : "tibia-border bg-card text-muted-foreground"
              }`}
            >
              {i}
            </button>
          ))}
        </div>
      </div>

      {/* Texto */}
      <div className="space-y-1">
        <label className="font-pixel text-[8px] text-muted-foreground">Comentário (opcional)</label>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          className="tibia-input rounded-sm w-full min-h-[60px] text-sm"
          placeholder="Descreva sua experiência..."
          maxLength={500}
        />
      </div>

      {/* Itens negociados */}
      <div className="space-y-1">
        <label className="font-pixel text-[8px] text-muted-foreground">Itens negociados *</label>
        {itensSelected.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {itensSelected.map((item) => (
              <span key={item} className="flex items-center gap-1 bg-primary/20 text-primary px-2 py-0.5 rounded-sm text-[7px] font-pixel">
                {item}
                <X className="w-2.5 h-2.5 cursor-pointer hover:text-destructive" onClick={() => setItensSelected(itensSelected.filter(i => i !== item))} />
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <input
            type="text"
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            className="tibia-input rounded-sm w-full text-sm"
            placeholder="Buscar item..."
          />
          {itemSearch && (
            <div className="absolute top-full left-0 right-0 mt-1 z-50 tibia-border bg-card rounded-sm max-h-[120px] overflow-auto shadow-lg">
              {filteredItems.length === 0 ? (
                <div className="px-3 py-2 text-[8px] text-muted-foreground font-pixel">Nenhum item</div>
              ) : (
                filteredItems.map((it) => {
                  const sel = itensSelected.includes(it.name);
                  return (
                    <button
                      key={it.name}
                      onClick={() => {
                        if (sel) setItensSelected(itensSelected.filter(i => i !== it.name));
                        else setItensSelected([...itensSelected, it.name]);
                        setItemSearch("");
                      }}
                      className="w-full flex items-center gap-2 px-2 py-1 hover:bg-primary/10 text-left cursor-pointer"
                    >
                      <img src={getItemImageUrl(it.name)} alt="" className="w-4 h-4 object-contain" onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }} />
                      <span className={`font-pixel text-[7px] flex-1 ${sel ? "text-primary" : "text-muted-foreground"}`}>{it.name}</span>
                      {sel && <Check className="w-3 h-3 text-primary" />}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data negociação */}
      <div className="space-y-1">
        <label className="font-pixel text-[8px] text-muted-foreground">Data da negociação (opcional)</label>
        <input
          type="date"
          value={dataNegociacao}
          onChange={(e) => setDataNegociacao(e.target.value)}
          className="tibia-input rounded-sm text-sm"
        />
      </div>

      {/* Anexos */}
      <div className="space-y-1">
        <label className="font-pixel text-[8px] text-muted-foreground">Comprovante(s) * (mín. 1 imagem)</label>
        {anexos.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-1">
            {anexos.map((f, i) => (
              <div key={i} className="relative group">
                <img src={URL.createObjectURL(f)} alt="" className="w-12 h-12 object-cover rounded-sm border border-border" />
                <button
                  onClick={() => removeAnexo(i)}
                  className="absolute -top-1 -right-1 bg-destructive text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px] opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
        <label className="tibia-border bg-card rounded-sm px-3 py-1.5 flex items-center gap-2 cursor-pointer hover:bg-primary/5 transition-colors">
          <Upload className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="font-pixel text-[7px] text-muted-foreground">Adicionar imagem</span>
          <input type="file" accept="image/*" multiple onChange={handleFileChange} className="hidden" />
        </label>
      </div>

      {/* Submit */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={handleSubmit}
        disabled={submitting}
        className="tibia-btn w-full rounded-sm flex items-center justify-center gap-2 disabled:opacity-40"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
          <>
            <Star className="w-3.5 h-3.5" />
            <span className="font-pixel text-[8px]">Enviar Avaliação</span>
          </>
        )}
      </motion.button>
    </div>
  );
};

export default AvaliarForm;
